import React from "react";
import type { ApolloClient } from "@apollo/client";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { connect, type ConnectedProps } from "react-redux";
import { push } from "store/actions/router";
import { RiExchangeDollarLine } from "react-icons/ri";
import { map } from "lodash";
import { employerConstants } from "actions/types";
import { categoryToColorMappingParticipationFunnel } from "utils/highcharts/config";
import { hasEnabledPayrollIntegration } from "store/selectors/employer";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { getEmployerTakeRateDownloadUrl } from "services/documentService";
import { simulateLinkClickNewTab } from "utils/dom";
import { wrapError } from "utils/errorHelper";
import { Card, Col, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import type { OverlayInjectedProps } from "react-bootstrap/esm/Overlay";
import {
  getAllEmployerGroups,
  getTakeRateEmployeesForCompany,
} from "actions/employerActions";
import {
  FiAlertCircle,
  FiClock,
  FiDownload,
  FiMail,
  FiTrendingUp,
} from "react-icons/fi";

import IconSpinner from "components/IconSpinner";
import Button from "components/Button";
import type { RootState } from "types/state";

import "./EmployerDashboard.scss";

const allGroups = {
  id: "",
  value: "ALL_GROUPS",
  name: "All Groups",
};

const dateFilter = [
  { value: "ALL_TIME", label: "All Time" },
  { value: "PAST_90_DAYS", label: "Past 90 Days" },
];

const updatingActions = [
  employerConstants.GET_EMPLOYER_GROUPS,
  employerConstants.GET_TAKE_RATE_EMPLOYEES,
];

const errorSelector = createErrorSelector(updatingActions);
const isUpdating = createLoadingSelector(updatingActions);

const mapStateToProps = (state: RootState) => {
  return {
    // employer.groups is shallow-typed as Array<Record<string, unknown>>
    // (that slice is scheduled for deletion)
    groups: state.employer.groups,
    isUpdating: isUpdating(state),
    error: errorSelector(state),
    hasPayrollIntegration: hasEnabledPayrollIntegration(state),
  };
};

const mapDispatchToProps = {
  push,
  getAllEmployerGroups,
  getTakeRateEmployeesForCompany,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type Props = WithApolloClient<ConnectedProps<typeof connector>>;

interface State {
  // present in initial state but never read; preserved verbatim
  isLoading: boolean;
  error: string;
  // present in initial state but never read; preserved verbatim
  initialFetching: boolean;
  selectedTakeRateGroup: string;
  selectedDate: string;
  result: { notStarted: number; opening: number; enrolled: number };
}

class EmployeePlanParticipation extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      isLoading: true,
      error: "",
      initialFetching: true,
      selectedTakeRateGroup: "ALL_GROUPS",
      selectedDate: "ALL_TIME",
      result: { notStarted: 0, opening: 0, enrolled: 0 },
    };
  }

  async componentDidMount() {
    window.analytics.page("Employer Dashboard");

    const selectedDate = this.getSelectedDate(this.state.selectedDate);
    const groupId = this.getGroupId(this.state.selectedTakeRateGroup);

    const result = await this._getTakeRateNumbers(groupId, selectedDate);

    this.setState({ result });
  }

  _getTakeRateNumbers = async (
    groupId: string | null,
    selectedDate: string
  ) => {
    const result = await this.props.getTakeRateEmployeesForCompany(
      this.props.client as ApolloClient<object>,
      groupId as string,
      selectedDate
    );
    return (result as { data: State["result"] }).data;
  };

  _createTakeRateGroupFilter = () => {
    const groups = this.props.groups.map((group) => {
      // group is shallow-typed as Record<string, unknown> so id/name are
      // unknown; cast to the strings they hold at runtime
      return {
        id: group.id as string,
        value: group.name as string,
        name: group.name as string,
      };
    });
    return [allGroups, ...groups];
  };

  _filterTakeRateGroups = (group: string) => {
    const allGroups = this.props.groups;
    if (group === "ALL_GROUPS") {
      return allGroups;
    }
    return this.props.groups.filter((groupId) => {
      return groupId.name === group;
    });
  };

  async _handleFilters(group: string, selectedDate: string) {
    const groupId = this.getGroupId(group);

    const filteredResult = await this._getTakeRateNumbers(
      groupId,
      this.getSelectedDate(selectedDate)
    );

    this.setState({ result: filteredResult });
  }

  _getPlanParticipationFunnel = (): Array<{
    id: keyof typeof categoryToColorMappingParticipationFunnel;
    enrollmentIcon: React.ReactNode;
    enrollmentLabel: string;
  }> => {
    {
      return [
        {
          id: "notStarted",
          enrollmentIcon: (
            <FiClock color={"#009deb"} stroke={"#FFFFFF"} size={"20px"} />
          ),
          enrollmentLabel: "Invited",
        },
        {
          id: "opening",
          enrollmentIcon: (
            <FiTrendingUp color={"#1a59e6"} stroke={"#FFFFFF"} size={"20px"} />
          ),
          enrollmentLabel: "Enrolling",
        },
        {
          id: "enrolled",
          enrollmentIcon: (
            <RiExchangeDollarLine
              fill={"#FFFFFF"}
              color={"#FFFFFF"}
              stroke={"#002c59"}
              strokeWidth={"0.5"}
              size={"22px"}
            />
          ),
          enrollmentLabel: "Enrolled",
        },
      ];
    }
  };

  _buildFunnel = () => {
    return map(
      this._getPlanParticipationFunnel(),
      (planParticipationFunnel) => (
        <Col
          className="enrollment"
          id={planParticipationFunnel.id}
          key={planParticipationFunnel.id}
        >
          <span
            className="enrollment-icon"
            style={{
              backgroundColor:
                categoryToColorMappingParticipationFunnel[
                  planParticipationFunnel.id
                ],
            }}
          >
            {planParticipationFunnel.enrollmentIcon}
          </span>

          <span className="enrollment-description">
            <p className="enrollment-label">
              {planParticipationFunnel.enrollmentLabel}
            </p>

            <p className="enrollment-value">
              {this.state.result[planParticipationFunnel.id]}
            </p>
          </span>
        </Col>
      )
    );
  };

  _renderTooltip = (props: OverlayInjectedProps) => (
    <Tooltip {...props}>
      <b>Document Download:</b>
      <br></br>
      The downloadable lists of employee emails will be updated daily.
    </Tooltip>
  );

  _fileDownload(status: string) {
    return (
      <>
        <a
          className="link"
          target="_blank"
          onClick={() =>
            this._downloadFile(
              this.state.selectedTakeRateGroup,
              status,
              this.state.selectedDate
            )
          }
        >
          <FiDownload
            fill={"#FFFFFF"}
            color={"#FFFFFF"}
            stroke={"#29b0c2"}
            strokeWidth={"1.5"}
            size={"16px"}
          />
        </a>
      </>
    );
  }

  getGroupId(groupName: string): string | null {
    if (groupName === "ALL_GROUPS") {
      return null;
    } else {
      const groupId = this._filterTakeRateGroups(groupName).map((group) => {
        // group is shallow-typed as Record<string, unknown> so id is unknown;
        // cast to the string it holds at runtime
        return group.id as string;
      });
      return groupId[0];
    }
  }

  getSelectedDate(selectedDate: string) {
    if (selectedDate === "Past 90 Days") return "PAST_90_DAYS";
    return "ALL_TIME";
  }

  _downloadFile(groupName: string, status: string, selectedDate: string) {
    const id = this.getGroupId(groupName);
    const date = this.getSelectedDate(selectedDate);

    getEmployerTakeRateDownloadUrl(this.props.client as ApolloClient<object>, {
      groupId: id as string,
      status,
      timeframe: date,
    })
      .then((response) => {
        // original accessed response.url unconditionally; preserve throw-on-null
        simulateLinkClickNewTab(response!.url);
      })
      .catch((error) => {
        this.setState({ error: wrapError(error) });
      });
  }

  render() {
    const content = this._buildFunnel();
    const isUpdating = this.props.isUpdating;
    return (
      <>
        <Card className="dashboard-widget">
          <div className="widget-header">
            <span>
              <Card.Title>Employee Plan Participation</Card.Title>
              <Card.Text>
                <span>
                  <Form.Select
                    className="group-filter"
                    id="inlineFormCustomSelect"
                    value={this.state.selectedTakeRateGroup}
                    onChange={(e) => {
                      void this._handleFilters(
                        e.target.value,
                        this.state.selectedDate
                      );

                      this.setState({
                        selectedTakeRateGroup: e.target.value,
                      });
                    }}
                  >
                    {map(this._createTakeRateGroupFilter(), (group) => (
                      <option id={group.id} value={group.value} key={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </Form.Select>
                </span>
                <span>
                  <Form.Select
                    className="group-filter"
                    id="inlineFormCustomSelect"
                    value={this.state.selectedDate}
                    onChange={(e) => {
                      void this._handleFilters(
                        this.state.selectedTakeRateGroup,
                        e.target.value
                      );
                      this.setState({
                        selectedDate: e.target.value,
                      });
                    }}
                  >
                    {map(dateFilter, (time) => (
                      <option key={time.value}>{time.label}</option>
                    ))}
                  </Form.Select>
                </span>
                <span className="OverlayTrigger">
                  <OverlayTrigger
                    placement="right"
                    delay={{ show: 250, hide: 200 }}
                    overlay={this._renderTooltip}
                    containerPadding={20}
                    transition={true}
                  >
                    <FiAlertCircle
                      className="FiAlertCircle"
                      size={"16px"}
                      color={"#FFFFFF"}
                      stroke={"#29b0c2"}
                      strokeWidth={"1.5"}
                    />
                  </OverlayTrigger>
                </span>
              </Card.Text>
            </span>
            {!this.props.hasPayrollIntegration && (
              <Button
                size="sm"
                name="action"
                color="action"
                icon={{
                  icon: <FiMail size={14} stroke={"#29b0c2"} />,
                  position: "left",
                }}
                onClick={() => {
                  this.props.push(`/dashboard/users/employees`);
                }}
                btnLabel="Invite Employees"
              />
            )}
          </div>
          {isUpdating && <IconSpinner centered />}
          {!isUpdating && (
            <div className="enrollment-table">
              <Row>{content}</Row>
              <Row className="bottom">
                <Col className="download">
                  {this.state.result.notStarted === 0
                    ? null
                    : this._fileDownload("NOT_STARTED")}
                </Col>
                <Col className="download">
                  {this.state.result.opening === 0
                    ? null
                    : this._fileDownload("OPENING")}
                </Col>
                <Col className="download">
                  {this.state.result.enrolled === 0
                    ? null
                    : this._fileDownload("ENROLLED")}
                </Col>
              </Row>
            </div>
          )}
        </Card>
      </>
    );
  }
}

export default connector(withApollo<Props>(EmployeePlanParticipation));
